import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import {mappers} from "../../utils/attributeMapping/mappers";
import {
  CANCER_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  NEUROLOGICAL_ATTRIBUTES,
  RESPIRATORY_ATTRIBUTES,
  VACCINE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Variant D of COVID-19 Vaccine Studies - Healthy Adults 50-64
 *
 * A/B test variant: Splits grouped checkbox exclusion questions into
 * individual yes/no radio questions — one per condition.
 * All other questions are identical to the control.
 *
 * Split questions: Q5, Q9, Q11, Q12, Q14, Q17
 *
 * Accessible at: /covid-vaccine-50-64-d
 */
export const covid50to64HealthyScreeningQuestionsD: PreScreeningQuestion[] = [
  // Q1: Age Eligibility
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "under_50",
        label: "Under 50",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64"
      },
      {
        value: "65_or_older",
        label: "65 or older",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is for adults aged 50 to 64.",
    attributeMapping: mappers.value({
      under_50: [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  // Q2: Height
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },

  // Q3: Weight
  {
    ...standardWeightQuestion,
    maxBMI: 40,
    disqualifyMessage: "This study requires a BMI less than 40 kg/m².",
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  // Q4: Hypertension
  {
    id: "hypertension",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>high blood pressure (hypertension)</strong>?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          if (bmi !== undefined && bmi <= 30) {
            return false;
          }
          return true;
        },
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "This study cannot include participants with high blood pressure and a BMI over 30.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
      ],
    }),
  },

  // === SPLIT Q5: Mood Disorder → 4 individual questions ===

  {
    id: "current_depression_symptoms",
    type: QuestionType.RADIO,
    question: "Do you currently have a diagnosis of <strong>depression</strong>, as diagnosed by a doctor or psychologist?",
    helpText: "Depression is formally diagnosed as major depressive disorder (MDD)",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain mood or mental health conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: false }],
    }),
  },
  {
    id: "bipolar_disorder",
    type: QuestionType.RADIO,
    question: "Do you have a current diagnosis of <strong>bipolar disorder</strong>, as diagnosed by a healthcare provider?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain mood or mental health conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_BIPOLAR, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_BIPOLAR, booleanAttributeValue: false }],
    }),
  },
  {
    id: "schizophrenia",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with <strong>schizophrenia</strong> by a healthcare provider?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain mood or mental health conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOPHRENIA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOPHRENIA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "schizoaffective_disorder",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>schizoaffective disorder</strong> by a healthcare provider?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain mood or mental health conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOAFFECTIVE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOAFFECTIVE, booleanAttributeValue: false }],
    }),
  },

  // Q6: Recent COVID Infection (unchanged)
  {
    id: "recent_covid_infection",
    type: QuestionType.RADIO,
    question: "In the past 3 months, have you had a <strong>positive COVID-19 test</strong> (PCR or antigen)?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a COVID-19 infection in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 24406, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 24406, booleanAttributeValue: false }],
    }),
  },

  // Q7: Recent COVID Vaccine (unchanged)
  {
    id: "recent_covid_vaccine",
    type: QuestionType.RADIO,
    question: "In the past 3 months, have you received any <strong>COVID-19 vaccine</strong>?",
    helpText: "This includes any COVID-19 vaccine such as Moderna, Pfizer, or Novavax.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, participants who have received a COVID-19 vaccine in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: false }],
    }),
  },

  // Q8: Diabetes (unchanged)
  {
    id: "diabetes",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>diabetes (Type 1 or Type 2)</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "type1", label: "Yes, Type 1 diabetes", disqualify: true },
      { value: "type2", label: "Yes, Type 2 diabetes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study is for adults without diabetes.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES, booleanAttributeValue: false }],
      type1: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true }],
      type2: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true }],
    }),
  },

  // === SPLIT Q9: Cardiovascular Conditions → 7 individual questions ===

  {
    id: "stroke_cva",
    type: QuestionType.RADIO,
    question: "Have you ever suffered a <strong>stroke, mini-stroke, or a transient ischemic attack (TIA)</strong>?",
    helpText: "A 'mini-stroke' or TIA is where stroke-like symptoms occur but resolve within 24 hours, leaving no lasting effects on the brain",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "heart_attack",
    type: QuestionType.RADIO,
    question: "Have you ever had a <strong>heart attack (myocardial infarction)</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false }],
    }),
  },
  {
    id: "heart_failure",
    type: QuestionType.RADIO,
    question: "Do you have <strong>heart failure</strong>?",
    helpText: "Heart failure is a condition where the heart can't pump blood well enough to meet the body's needs, leading to fatigue, shortness of breath, and swelling; diagnosed through an ultrasound of the heart (echocardiogram).",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "coronary_artery_disease",
    type: QuestionType.RADIO,
    question: "Do you have <strong>coronary artery disease</strong> as diagnosed by a doctor? (Also known as ischemic heart disease)",
    helpText: "This might include having had a heart attack in the past, or narrowed coronary arteries associated with chest pain, diagnosed through a heart scan, angiogram or stress test",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false }],
    }),
  },
  {
    id: "heart_inflammation_recent",
    type: QuestionType.RADIO,
    question: "Have you suffered from suspected or confirmed <strong>myocarditis, pericarditis or myopericarditis</strong> in the past 3 months?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25888, booleanAttributeValue: true }], // history_myocarditis_pericarditis_prerequisite
      no: [{ attributeDefinitionId: 25888, booleanAttributeValue: false }],
    }),
  },
  {
    id: "cardiomyopathy",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>cardiomyopathy</strong> by a doctor?",
    helpText: "Cardiomyopathy is a heart condition where the heart muscle becomes weakened, enlarged, or stiffened, affecting its ability to pump blood",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23518, booleanAttributeValue: true }], // has_cardiomyopathy
      no: [{ attributeDefinitionId: 23518, booleanAttributeValue: false }],
    }),
  },
  {
    id: "pulmonary_hypertension",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>pulmonary hypertension</strong> by a doctor?",
    helpText: "Pulmonary hypertension is a specific type of high blood pressure that affects the arteries in your lungs, distinct from regular hypertension which affects the rest of the body",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 8249, booleanAttributeValue: true }], // has_pulmonaryhypertension
      no: [{ attributeDefinitionId: 8249, booleanAttributeValue: false }],
    }),
  },

  // Q10: Asthma (unchanged)
  {
    id: "asthma",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>asthma</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes_moderate_severe", label: "Yes, asthma requiring daily medication", disqualify: true },
      { value: "yes_mild", label: "Yes, mild asthma (no daily medication)" },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with moderate to severe asthma requiring daily preventer medication.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false }],
      yes_mild: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true }],
      yes_moderate_severe: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true }],
    }),
  },

  // === SPLIT Q11: Chronic Lung Disease → 4 individual questions ===

  {
    id: "copd",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>COPD</strong> by a healthcare provider?",
    helpText: "COPD (chronic obstructive pulmonary disease) is a chronic lung condition that includes emphysema and chronic bronchitis",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with chronic lung disease.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: false }],
    }),
  },
  {
    id: "interstitial_lung_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>lung fibrosis or interstitial lung disease (ILD)</strong> by a doctor?",
    helpText: "ILD is a group of conditions that cause lung fibrosis (scarring). Examples include Idiopathic pulmonary fibrosis (IPF) and autoimmune lung disease. This does not include COPD",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with chronic lung disease.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25661, booleanAttributeValue: true }], // has_ILD_prerequisite
      no: [{ attributeDefinitionId: 25661, booleanAttributeValue: false }],
    }),
  },
  {
    id: "pulmonary_embolism",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with a <strong>pulmonary embolism (PE)</strong>?",
    helpText: "A pulmonary embolism is a blood clot in the lungs, diagnosed on a scan and treated with blood thinning medication",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with chronic lung disease.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_BLOOD_CLOT, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_BLOOD_CLOT, booleanAttributeValue: false }],
    }),
  },
  {
    id: "cystic_fibrosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>cystic fibrosis</strong> by a doctor?",
    helpText: "Cystic fibrosis is a genetic disorder usually diagnosed in childhood that affects the lungs and digestive system",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with chronic lung disease.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25767, booleanAttributeValue: true }], // has_CF_prerequisite
      no: [{ attributeDefinitionId: 25767, booleanAttributeValue: false }],
    }),
  },

  // === SPLIT Q12: Immunocompromised Conditions → 3 individual questions ===

  {
    id: "organ_transplant",
    type: QuestionType.RADIO,
    question: "Have you ever had an <strong>organ transplant or are you scheduled to undergo</strong> (or currently on a waitlist for) any solid organ transplant?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who are immunocompromised.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false }],
    }),
  },
  {
    id: "immunosuppressive_medications",
    type: QuestionType.RADIO,
    question: "Do you take <strong>immunosuppressing medications</strong>?",
    helpText: "Such as prednisone, methotrexate, azathioprine, cyclosporine, mycophenolate, or rituximab.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants taking immunosuppressive medications.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25884, booleanAttributeValue: true }], // is_immunocompromised_prerequisite
      no: [{ attributeDefinitionId: 25884, booleanAttributeValue: false }],
    }),
  },
  {
    id: "active_tb",
    type: QuestionType.RADIO,
    question: "Do you have active <strong>tuberculosis (TB)</strong> or are you currently receiving treatment for TB?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with active tuberculosis.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: false }],
    }),
  },

  // Q13: HIV Status (unchanged)
  {
    id: "hiv_status",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>HIV</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "uncontrolled", label: "Yes, my HIV is not well-controlled or I am not currently on treatment", disqualify: true },
      {
        value: "controlled",
        label: "Yes, my HIV is well-controlled (stable on antiretroviral medication with undetectable viral load)",
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          if (bmi === undefined) return true;
          return bmi >= 30;
        },
      },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "Based on your answers, you are not eligible for any of the studies we are currently recruiting for.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false }],
      controlled: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        { attributeDefinitionId: 23426, booleanAttributeValue: false },
      ],
      uncontrolled: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        { attributeDefinitionId: 23426, booleanAttributeValue: true },
      ],
    }),
  },

  // === SPLIT Q14: Neurological Conditions → 2 individual questions ===

  {
    id: "dementia",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>dementia</strong> by a doctor?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain neurological conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEMENTIA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEMENTIA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "parkinsons",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>Parkinson's disease</strong> by a doctor?",
    helpText: "Parkinson's disease is a condition that affects movement and can cause tremors, stiffness, and balance problems.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with certain neurological conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_PARKINSONS, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_PARKINSONS, booleanAttributeValue: false }],
    }),
  },

  // Q15: ADHD Diagnosis (unchanged)
  {
    id: "adhd_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed by a healthcare provider with <strong>ADHD (attention-deficit/hyperactivity disorder)</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with a diagnosis of ADHD.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ADHD, booleanAttributeValue: false }],
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ADHD, booleanAttributeValue: true }],
    }),
  },

  // Q16: Smoking Status (unchanged)
  {
    id: "smoking_status",
    type: QuestionType.RADIO,
    question: "Do you <strong>currently smoke</strong> cigarettes, e-cigarettes or vapes, or <strong>have you regularly smoked</strong> cigarettes, e-cigarettes or vapes in the past?",
    helpText: "\"Regularly\" means smoking most days or as part of a routine.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes_regularly", label: "Yes, currently smoke", disqualify: true },
      { value: "quit_within_6_months", label: "Quit within the past 6 months", disqualify: true },
      {
        value: "quit_over_6_months",
        label: "Quit more than 6 months ago",
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          if (bmi === undefined) return true;
          return bmi >= 30;
        },
      },
      { value: "no", label: "No, never regularly smoked" },
    ],
    disqualifyMessage: "Based on your answers, you are not eligible for any of the studies we are currently recruiting for. Current smokers and those who quit within the past 6 months cannot participate.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: false },
      ],
      quit_over_6_months: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: true },
      ],
      quit_within_6_months: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: false },
      ],
      yes_regularly: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
      ],
    }),
  },

  // === SPLIT Q17: Other Exclusionary Conditions → 5 individual questions ===

  {
    id: "cancer_recent",
    type: QuestionType.RADIO,
    question: "In the past 5 years, have you been diagnosed with any type of <strong>cancer</strong> other than skin cancer (BCC/basal cell carcinoma or SCC/squamous cell carcinoma)?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false }],
    }),
  },
  {
    id: "guillain_barre",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with <strong>Guillain-Barre syndrome (GBS)</strong> by a doctor?",
    helpText: "GBS is a rare condition where your immune system attacks your nerves, leading to muscle weakness and sometimes paralysis.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 25742, booleanAttributeValue: true }], // had_guillianbarre_prerequisite
      no: [{ attributeDefinitionId: 25742, booleanAttributeValue: false }],
    }),
  },
  {
    id: "chronic_kidney_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>chronic kidney disease (CKD)</strong> by a doctor?",
    helpText: "CKD is a long-term condition that is characterized by persistent kidney damage or reduced kidney function, and is usually diagnosed with a blood test",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "liver_disease",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>liver failure, cirrhosis, fatty liver disease, or NASH</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false }],
    }),
  },
  {
    id: "hepatitis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with <strong>hepatitis B or hepatitis C</strong>?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
      ],
    }),
  },

  // Q18: Concurrent Trial (unchanged)
  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
    }),
  },
];
